/**
 * Auth Service
 * Business logic for authentication operations
 */

import { getAuth, getFirestore, setCustomClaims, timestamp, FieldValue } from '../../core/database/firebase.js';
import { verifyAuth0Token, type AuthUser } from '../../core/auth/auth0.js';
import type { AdminUserPlanInfo, AuthUserProfile } from '@my-roadtrip/types';
import { createLogger } from '../../common/utils/logger.js'
import { createBatchBuilder } from '../../common/utils/firestore-batch.js'
import { toDateSafe, toDateOrNow } from '../../common/utils/normalize.js'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'


const logger = createLogger('AuthService')

interface ProfileUpdate {
  displayName?: string;
  photoURL?: string;
  preferences?: Record<string, unknown>;
}

function normalizePlan(input: unknown): AdminUserPlanInfo {
  const defaultPlan: AdminUserPlanInfo = { tier: 'free', name: 'Free' }

  if (input && typeof input === 'object') {
    const candidate = input as Partial<AdminUserPlanInfo>
    if (candidate.tier === 'free' || candidate.tier === 'premium' || candidate.tier === 'pro') {
      const plan: AdminUserPlanInfo = {
        tier: candidate.tier,
        name: typeof candidate.name === 'string' && candidate.name.length > 0
          ? candidate.name
          : candidate.tier === 'premium'
            ? 'Premium'
            : candidate.tier === 'pro'
              ? 'Pro'
              : 'Free',
      }

      if (isString(candidate.id)&& candidate.id.length > 0) {
        plan.id = candidate.id
      }

      return plan
    }
  }

  if (isString(input)&& input.length > 0) {
    const tier = (['free', 'premium', 'pro'] as const).includes(input as any) ? (input as AdminUserPlanInfo['tier']) : 'free'
    return {
      tier,
      name: tier === 'premium' ? 'Premium' : tier === 'pro' ? 'Pro' : 'Free'
    }
  }

  return defaultPlan
}

// Default icon options for new users - MUST match ProfileIconId and ProfileIconColor from @my-roadtrip/types
const VALID_PROFILE_ICONS = [
  'hiking', 'explore', 'terrain', 'flight_takeoff', 'bus',
  'directions_car', 'two_wheeler', 'pedal_bike', 'backpack',
  'camera_alt', 'landscape', 'beach_access', 'forest',
  'snowflake', 'waves', 'volunteer_activism', 'group', 'favorite'
] as const

const VALID_PROFILE_COLORS = [
  'blue', 'purple', 'pink', 'green', 'orange', 'red', 'teal', 'amber', 'cyan'
] as const

function getRandomElement<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function generateDefaultIconSet() {
  return {
    activeIcon: getRandomElement([...VALID_PROFILE_ICONS]),
    accentColor: getRandomElement([...VALID_PROFILE_COLORS])
  }
}

function generateDisplayName(authUser: AuthUser): string {
  // Priority: name > nickname > email prefix > default
  if (authUser.name && authUser.name.trim()) {
    return authUser.name.trim()
  }
  if (authUser.nickname && authUser.nickname.trim()) {
    return authUser.nickname.trim()
  }
  if (authUser.email) {
    // Extract name from email (before @)
    const emailPrefix = authUser.email.split('@')[0]
    // Clean up: replace dots/underscores with spaces, capitalize
    return emailPrefix
      .replace(/[._-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim()
  }
  return 'Voyageur'
}

class AuthService {
  private get db() {
    return getFirestore();
  }

  private get auth() {
    return getAuth();
  }


  /**
   * Verify Auth0 access token
   */
  async verifyToken(idToken: string): Promise<AuthUser> {
    logger.debug('Verifying Auth0 token')
    const authUser = await verifyAuth0Token(idToken)
    logger.debug('Auth0 token verified', { userId: authUser.uid, email: authUser.email })
    return authUser
  }

  /**
   * Sync Auth0 user to Firestore (create or update)
   * This should be called after Auth0 login to ensure user exists in our DB
   * 
   * ⚠️ CRITICAL: Firestore is the single source of truth for:
   * - plan (managed by admin or billing webhooks)
   * - subscriptionStatus (managed by admin or billing webhooks)
   * - roles/role/admin (managed by admin)
   * - status/suspendedUntil/suspensionReason (managed by admin)
   * - quota (managed by billing service)
   * 
   * This function ONLY updates:
   * - email, emailVerified, displayName, photoURL (from Auth0)
   * - lastLoginAt, updatedAt (timestamps)
   * - Creates user with defaults if doesn't exist
   */
  async syncAuth0User(authUser: AuthUser): Promise<AuthUserProfile> {
    const uid = authUser.uid;
    const userDoc = await this.db.collection('users').doc(uid).get();

    const now = timestamp.now();

    if (userDoc.exists) {
      // ✅ EXISTING USER: Only update Auth0-sourced fields + timestamps
      // ⚠️ NEVER overwrite: plan, subscriptionStatus, roles, status, quota
      const existingData = userDoc.data()!;
      
      // Generate display name with fallbacks
      const resolvedDisplayName = generateDisplayName(authUser) || existingData.displayName || 'Voyageur'
      
      const safeUpdates: Record<string, unknown> = {
        email: authUser.email || existingData.email || null,
        emailVerified: authUser.emailVerified || false,
        displayName: resolvedDisplayName,
        photoURL: authUser.picture || existingData.photoURL || null,
        picture: authUser.picture || existingData.picture || null,
        lastLoginAt: now,
        updatedAt: now,
      };

      // Initialize iconSet if it doesn't exist (migration for old users)
      if (!existingData.iconSet) {
        safeUpdates.iconSet = generateDefaultIconSet()
        logger.info('Generated default iconSet for existing user', { uid })
      }

      // Initialize gamification if it doesn't exist (migration for old users)
      if (existingData.gamification === undefined) {
        safeUpdates.gamification = {
          points: 0,
          level: 1,
          badges: [],
          stats: {
            plansCreated: 0,
            stagesCreated: 0,
            totalDistance: 0,
            totalDuration: 0,
            countriesVisited: 0,
            citiesVisited: 0,
            sharedPlans: 0,
            collaborations: 0
          }
        };
      }

      // Update in background - don't wait for it
      void this.db.collection('users').doc(uid).update(safeUpdates);
      
      // Return profile immediately from existing data (avoid second fetch)
      return this.buildProfileFromData(uid, { ...existingData, ...safeUpdates });
    } else {
      // ✅ NEW USER: Create with Auth0 data + defaults
      const planValue = normalizePlan(authUser.plan);
      const resolvedDisplayName = generateDisplayName(authUser)
      
      const newUserData = {
        id: uid,
        uid,
        email: authUser.email || null,
        emailVerified: authUser.emailVerified || false,
        displayName: resolvedDisplayName,
        photoURL: authUser.picture || null,
        picture: authUser.picture || null,
        iconSet: generateDefaultIconSet(), // 🆕 Always create with default iconSet
        role: authUser.role || 'user',
        roles: [authUser.role || 'user'],
        admin: authUser.admin || false,
        premium: authUser.premium || false,
        status: 'active',
        subscriptionStatus: authUser.subscriptionStatus || 'inactive',
        plan: planValue,
        createdAt: now,
        lastLoginAt: now,
        updatedAt: now,
        gamification: {
          points: 0,
          level: 1,
          badges: [],
          stats: {
            plansCreated: 0,
            stagesCreated: 0,
            totalDistance: 0,
            totalDuration: 0,
            countriesVisited: 0,
            citiesVisited: 0,
            sharedPlans: 0,
            collaborations: 0
          }
        },
        preferences: null
      };
      
      await this.db.collection('users').doc(uid).set(newUserData);
      
      // Return profile directly from created data (avoid second fetch)
      return this.buildProfileFromData(uid, newUserData);
    }
  }

  /**
   * Build profile response from raw Firestore data
   * Avoids redundant fetch after sync
   */
  private buildProfileFromData(uid: string, data: Record<string, unknown>): AuthUserProfile {
    const gamification = (data.gamification as Record<string, unknown>) || {
      points: 0,
      level: 1,
      badges: [],
      stats: {
        plansCreated: 0,
        stagesCreated: 0,
        totalDistance: 0,
        totalDuration: 0,
        countriesVisited: 0,
        citiesVisited: 0,
        sharedPlans: 0,
        collaborations: 0
      }
    };
    const plan = normalizePlan(data.plan);

    return {
      id: uid,
      uid,
      email: data.email as string,
      emailVerified: data.emailVerified as boolean,
      displayName: data.displayName as string,
      photoURL: data.photoURL as string,
      picture: data.photoURL as string,
      bio: data.bio as string,
      roles: (data.roles as AuthUserProfile['roles']) || [data.role as string || 'user'],
      status: (data.status as AuthUserProfile['status']) || 'active',
      plan,
      quota: (data.quota as AuthUserProfile['quota']) || { current: { plansGenerated: 0, aiRequests: 0 }, limits: { plansPerMonth: 5, aiRequestsPerDay: 10 } },
      subscriptionStatus: (data.subscriptionStatus as string) || 'inactive',
      gamification: gamification as AuthUserProfile['gamification'],
      preferences: (data.preferences as AuthUserProfile['preferences']) || {
        userId: uid,
        travel: { defaultTransport: 'car', defaultPace: 'normal', avoidTolls: false, avoidHighways: false, avoidFerries: false },
        display: { theme: 'auto', language: 'fr', units: 'metric' },
        notifications: { push: true, email: true, emailFrequency: 'daily', types: { tripReminders: true, socialActivity: true, achievements: true, planUpdates: true } },
        privacy: {
          profilePublic: false,
          showAvatar: true,
          showBio: true,
          showLocation: false,
          showStats: true,
          allowPublicTrips: true,
          allowLocationTracking: true,
          allowAnalytics: true,
          shareLiveWithFriends: true,
          shareLiveWithClans: true
        },
        security: { twoFactorEnabled: false, twoFactorMethod: null }
      },
      languages: (data.languages as string[]) || [],
      interests: (data.interests as string[]) || [],
      planCount: (data.planCount as number) || 0,
      shareCount: (data.shareCount as number) || 0,
      flagsCount: (data.flagsCount as number) || 0,
      createdAt: toDateOrNow(data.createdAt),
      updatedAt: toDateOrNow(data.updatedAt),
      lastLoginAt: toDateOrNow(data.lastLoginAt),
      deletedAt: toDateSafe(data.deletedAt),
      customClaims: {
        premium: (data.premium as boolean) || false,
        admin: (data.admin as boolean) || false,
        subscriptionStatus: (data.subscriptionStatus as string) || 'inactive',
        plan: plan.tier,
      },
    };
  }

  /**
   * Get user profile from Firestore
   * Returns null if user doesn't exist (use syncAuth0User first)
   */
  async getUserProfile(uid: string): Promise<AuthUserProfile> {
    const userDoc = await this.db.collection('users').doc(uid).get();

    if (!userDoc.exists) {
      throw new Error(`User not found in Firestore: ${uid}. Call syncAuth0User first.`);
    }

    const data = userDoc.data()!;

    // Ensure gamification exists (for old users migrated before gamification)
    const gamification = data.gamification || {
      points: 0,
      level: 1,
      badges: [],
      stats: {
        plansCreated: 0,
        stagesCreated: 0,
        totalDistance: 0,
        totalDuration: 0,
        countriesVisited: 0,
        citiesVisited: 0,
        sharedPlans: 0,
        collaborations: 0
      }
    };
    const plan = normalizePlan(data.plan);

    return {
      id: uid,
      uid,
      email: data.email,
      emailVerified: data.emailVerified,
      displayName: data.displayName,
      photoURL: data.photoURL,
      picture: data.photoURL,
      bio: data.bio,
      roles: data.roles || [data.role || 'user'],
      status: data.status || 'active',
      plan,
      quota: data.quota || { current: { plansGenerated: 0, aiRequests: 0 }, limits: { plansPerMonth: 5, aiRequestsPerDay: 10 } },
      subscriptionStatus: data.subscriptionStatus || 'inactive',
      gamification,
      preferences: data.preferences || {
        userId: uid,
        travel: { defaultTransport: 'car', defaultPace: 'normal', avoidTolls: false, avoidHighways: false, avoidFerries: false },
        display: { theme: 'auto', language: 'fr', units: 'metric' },
        notifications: { push: true, email: true, emailFrequency: 'daily', types: { tripReminders: true, socialActivity: true, achievements: true, planUpdates: true } },
        privacy: {
          profilePublic: false,
          showAvatar: true,
          showBio: true,
          showLocation: false,
          showStats: true,
          allowPublicTrips: true,
          allowLocationTracking: true,
          allowAnalytics: true,
          shareLiveWithFriends: true,
          shareLiveWithClans: true
        },
        security: { twoFactorEnabled: false, twoFactorMethod: null }
      },
      languages: data.languages || [],
      interests: data.interests || [],
      planCount: data.planCount || 0,
      shareCount: data.shareCount || 0,
      flagsCount: data.flagsCount || 0,
      createdAt: toDateOrNow(data.createdAt),
      updatedAt: toDateOrNow(data.updatedAt),
      lastLoginAt: toDateOrNow(data.lastLoginAt),
      deletedAt: toDateSafe(data.deletedAt),
      customClaims: {
        premium: data.premium || false,
        admin: data.admin || false,
        subscriptionStatus: data.subscriptionStatus || 'inactive',
        plan: plan.tier,
      },
    };
  }

  /**
   * Update user profile in Firestore
   */
  async updateUserProfile(uid: string, updates: ProfileUpdate): Promise<AuthUserProfile> {
    logger.debug('Updating user profile', { userId: uid, fields: Object.keys(updates) });
    const updateData: Record<string, unknown> = {
      ...updates,
      updatedAt: timestamp.now(),
    };

    await this.db.collection('users').doc(uid).update(updateData);

    // Update Firebase Auth profile if displayName or photoURL changed
    if (updates.displayName || updates.photoURL) {
      await this.auth.updateUser(uid, {
        displayName: updates.displayName,
        photoURL: updates.photoURL,
      });
    }

    logger.info('User profile updated', { userId: uid });
    return this.getUserProfile(uid);
  }

  /**
   * Refresh custom claims from Firestore
   * Used after subscription changes or role updates
   */
  async refreshCustomClaims(uid: string): Promise<Record<string, unknown>> {
    logger.debug('Refreshing custom claims from Firestore', { userId: uid });
    const userDoc = await this.db.collection('users').doc(uid).get();
    if (!userDoc.exists) {
      throw new Error('User not found')
    }
    const data = userDoc.data()!;

    const claims = {
      premium: data.premium || false,
      admin: data.admin || false,
      subscriptionStatus: data.subscriptionStatus || 'inactive',
      plan: typeof data.plan === 'string' ? data.plan : 'free',
    };

    await setCustomClaims(uid, claims);
    logger.info('Custom claims updated', { userId: uid, premium: claims.premium, admin: claims.admin });
    return claims;
  }

  /**
   * Delete user account (soft delete)
   * Anonymizes data instead of hard delete for GDPR compliance
   */
  async deleteUserAccount(uid: string): Promise<void> {
    logger.warn('Soft deleting user account', { userId: uid });
    // Anonymize user data
    await this.db.collection('users').doc(uid).update({
      email: `deleted_${uid}@example.com`,
      displayName: 'Deleted User',
      photoURL: FieldValue.delete(),
      deletedAt: timestamp.now(),
      preferences: FieldValue.delete(),
    });

    // Transfer or delete user's plans
    const plansSnapshot = await this.db
      .collection('plans')
      .where('userId', '==', uid)
      .get();

    const batch = createBatchBuilder(this.db);
    for (const doc of plansSnapshot.docs) {
      batch.updateRef(doc.ref, {
        userId: `deleted_${uid}`,
        deletedAt: timestamp.now(),
      });
    }
    await batch.commit();

    // Disable Firebase Auth account
    await this.auth.updateUser(uid, {
      disabled: true,
    });

    logger.info('User account disabled and anonymized', { userId: uid });
  }

  /**
   * Set premium status for user
   * Called by subscription webhook
   */
  async setPremiumStatus(
    uid: string,
    premium: boolean,
    subscriptionStatus: string
  ): Promise<void> {
    logger.debug('Updating premium status', { userId: uid, premium, subscriptionStatus });
    await this.db.collection('users').doc(uid).update({
      premium,
      subscriptionStatus,
      updatedAt: timestamp.now(),
    });

    await this.refreshCustomClaims(uid);
    logger.info('Premium status updated', { userId: uid, premium, subscriptionStatus });
  }

  /**
   * Set admin role for user
   * Only callable by existing admins
   */
  async setAdminRole(uid: string, admin: boolean): Promise<void> {
    logger.debug('Updating admin role', { userId: uid, admin });
    await this.db.collection('users').doc(uid).update({
      admin,
      updatedAt: timestamp.now(),
    });

    await this.refreshCustomClaims(uid);
    logger.info('Admin role updated', { userId: uid, admin });
  }
}

export const authService = new AuthService();
